// Town coverage pages content — one entry per slug in TOWNS.
// Feeds TownCoverageTemplate via /coverage/:slug routing. Aims for 500–700
// useful words per page, using safe "we cover" / "we can help" wording.
// No invented clients, no invented response times, no claims of past work
// at specific named industrial estates.

export const TOWN_PAGES = {
  // ---------------------------------------------------------------------------
  // Manchester (Greater Manchester)
  // ---------------------------------------------------------------------------
  manchester: {
    slug: "manchester",
    name: "Manchester",
    region: "greater-manchester",
    regionName: "Greater Manchester",
    heroImage: "/gallery/db-panel-3.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in Manchester.",
    subhead:
      "Mechanical, electrical and controls support for Manchester and its industrial and commercial districts — planned and reactive works, single-supplier.",
    seo: {
      title: "Industrial & Commercial Engineering in Manchester",
      description:
        "Mechanical, electrical, controls and compliance engineering for Manchester industrial and commercial sites. Single-supplier support from Lutrom's Northwich base.",
    },
    intro: `Manchester is the commercial and industrial heart of the region, with a footprint that runs from the city-centre commercial estates and the Ship Canal industrial corridor out to the Airport City logistics cluster, the northern industrial parks and the eastern warehousing belt. The mix of sites is unusually broad: multi-story city-centre commercial, food and beverage production in the outer boroughs, logistics and distribution along the M60 orbital, warehousing and light industrial across Trafford and Salford, and a large hospitality, retail and event-venue estate through the centre. Each of those environments has a different rhythm — trading hours in retail, 24/7 operations at the airport, tight batch windows in food production — and each needs to be planned around rather than fought. Lutrom covers Manchester and its wider metropolitan footprint from our Northwich workshop, with engineers set up to work under the city's typical constraints: parking permits and loading restrictions, security-controlled access, planned isolation windows in older shared-service buildings, and out-of-hours attendance where trading or operating hours make daytime work impractical. We can help with new installations, rolling maintenance, compliance testing (EICR, PAT, fire-alarm servicing) and reactive breakdown response.`,
    typicalWork: [
      "Multi-site EICR / fixed-wire testing for city-centre and orbital estates",
      "Fire-alarm and emergency-lighting servicing to statutory intervals",
      "Industrial electrical installation for manufacturing and logistics sites",
      "Control panel design, build and installation for line equipment",
      "PPM contracts across mechanical, electrical and controls",
      "Reactive callout cover for line-down and safety-critical faults",
    ],
    servicesFocus: [
      "eicr-and-fixed-wire-testing",
      "planned-preventive-maintenance",
      "industrial-electrical-installation",
      "24-7-emergency-breakdown-callout",
    ],
    coverageNote:
      "Manchester is well-served by the M6/M60/M62 network from our Northwich base. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "Do you cover the whole of Manchester or just the city centre?",
        a: "We cover Manchester and its wider metropolitan footprint — the city itself, the surrounding boroughs and the industrial and commercial estates that ring the orbital. Where a site is in an outer borough with its own coverage page (Salford, Bolton), that page will normally give the most relevant local detail.",
      },
      {
        q: "Can you attend out-of-hours in Manchester?",
        a: "Yes — out-of-hours attendance is common for retail, hospitality and city-centre commercial sites where daytime work isn't practical. The out-of-hours premium is priced up-front at the point of quote so there are no surprises on the invoice.",
      },
      {
        q: "How do you handle parking, loading and permits in the city centre?",
        a: "City-centre parking, loading windows and any building-specific permit-to-work are agreed in advance. Where a site has an existing permit or contractor-induction regime we work within that; where it doesn't, our own RAMS and permits apply.",
      },
      {
        q: "Do you offer PPM contracts for Manchester multi-site portfolios?",
        a: "Yes — multi-site portfolios (retail estates, hospitality groups, MATs, commercial landlord estates) are consolidated into a single account with per-site schedules and portfolio-level reporting for head office.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Liverpool (Merseyside)
  // ---------------------------------------------------------------------------
  liverpool: {
    slug: "liverpool",
    name: "Liverpool",
    region: "merseyside",
    regionName: "Merseyside",
    heroImage: "/gallery/extra-05.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in Liverpool.",
    subhead:
      "Mechanical, electrical and controls support for Liverpool's industrial, port, logistics and commercial sites — planned or reactive.",
    seo: {
      title: "Industrial & Commercial Engineering in Liverpool",
      description:
        "Mechanical, electrical, controls and compliance engineering for Liverpool industrial, port and commercial sites. Single-supplier support from Lutrom's Northwich base.",
    },
    intro: `Liverpool's industrial and commercial footprint is defined by three pillars: the port and its logistics belt running north through Seaforth and Bootle, the automotive supply-chain corridor running south through Speke and Halewood, and the city-centre commercial and hospitality estate that supports the port and airport traffic. Each of those environments imposes its own operating pattern — the port and its supporting logistics parks run around ship arrival schedules, the automotive corridor operates on tier-two supply-chain rhythms with tight delivery windows, and the city-centre commercial estate expects work outside trading hours. Lutrom covers Liverpool and the wider Merseyside footprint from our Northwich workshop, with engineers who can operate under port-security regimes, automotive supply-chain audit requirements and city-centre out-of-hours constraints. We can help with electrical installation and rewires, control-panel work for logistics and manufacturing lines, PPM contracts across mechanical and electrical scope, and rapid reactive response for line-down or estate-critical failures. We also cover the compliance work — EICR, PAT, fire-alarm and emergency-lighting servicing — that most Liverpool sites need running on a scheduled cycle for insurance and audit purposes.`,
    typicalWork: [
      "Industrial electrical installation for port, logistics and manufacturing sites",
      "Control-panel design, build and installation to BS EN 61439",
      "Fire-alarm and emergency-lighting servicing to statutory intervals",
      "EICR / fixed-wire testing on a rolling schedule",
      "PPM contracts across mechanical, electrical and controls",
      "Reactive callout cover for time-critical breakdowns",
    ],
    servicesFocus: [
      "industrial-electrical-installation",
      "control-panel-design-and-build",
      "eicr-and-fixed-wire-testing",
      "planned-preventive-maintenance",
    ],
    coverageNote:
      "Liverpool is within straightforward reach of our Northwich base via the M53/M62. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "Do you cover the port and its supporting industrial estates?",
        a: "Yes — where the site's security regime allows external contractors, we're set up to work under port-controlled access. Please tell us at enquiry stage what induction, security and permit requirements apply and we'll confirm fit before we attend.",
      },
      {
        q: "Can you work on automotive supply-chain sites?",
        a: "Yes. Tier-two and tier-three automotive supply-chain sites typically have their own audit and delivery-window requirements; we work within those and produce our documentation in the format the audit programme expects.",
      },
      {
        q: "Do you offer 24/7 reactive callout in Liverpool?",
        a: "Yes — 24/7 reactive callout is available across Merseyside. Response performance depends on distance from Northwich and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
      },
      {
        q: "Can you take on multi-site accounts across Liverpool?",
        a: "Yes. Multi-site accounts are consolidated into a single register with per-site schedules and one invoice, so head office sees the whole estate rather than chasing individual sites.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Warrington (Cheshire)
  // ---------------------------------------------------------------------------
  warrington: {
    slug: "warrington",
    name: "Warrington",
    region: "cheshire",
    regionName: "Cheshire",
    heroImage: "/gallery/db-panel-2.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in Warrington.",
    subhead:
      "Mechanical, electrical and controls support for Warrington's manufacturing, logistics and commercial sites — one of our closest core coverage towns.",
    seo: {
      title: "Industrial & Commercial Engineering in Warrington",
      description:
        "Mechanical, electrical, controls and compliance engineering for Warrington manufacturing, logistics and commercial sites. Close to Lutrom's Northwich base.",
    },
    intro: `Warrington sits at the road-network crossroads of the North West — the M6, M56 and M62 all meet within its footprint, which is exactly why the town has become one of the densest logistics and manufacturing clusters in the region. The mix includes large distribution and warehousing operations, mid-size manufacturing across food and beverage, automotive supply, chemical and general engineering, plus a substantial commercial and hospitality estate around the town centre. Warrington is one of the closer major towns to our Northwich workshop, which usually makes it one of our more responsive coverage areas for reactive work. Lutrom covers Warrington with the full range of mechanical, electrical and controls engineering — new installations, machine work, control-panel builds, PPM contracts and compliance testing. We can also help with the reactive, out-of-hours breakdown response that logistics and F&B sites often need most, and with the rolling multi-site EICR / PAT / fire-alarm cycles that Warrington's commercial estates typically need running.`,
    typicalWork: [
      "Industrial electrical installation for manufacturing and logistics sites",
      "Machine installation, refurbishment and commissioning",
      "Control-panel design, build and installation to BS EN 61439",
      "PPM contracts across mechanical, electrical and controls",
      "EICR / fixed-wire testing and PAT for commercial estates",
      "Reactive breakdown callout for line-down electrical or mechanical failures",
    ],
    servicesFocus: [
      "machine-installation-and-commissioning",
      "industrial-electrical-installation",
      "control-panel-design-and-build",
      "24-7-emergency-breakdown-callout",
    ],
    coverageNote:
      "Warrington is one of our closest core coverage towns from our Northwich base. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "How does Warrington compare to your other coverage towns?",
        a: "Warrington is one of the shorter drives from our Northwich workshop, which usually makes it one of our more responsive coverage areas for reactive work. Planned work is scheduled the same way as anywhere else in our footprint — around your production or trading calendar rather than ours.",
      },
      {
        q: "Do you cover the industrial estates on the outskirts of Warrington?",
        a: "Yes — the industrial parks and business estates that ring the M6/M62 corridor are inside our normal coverage. Where a specific site has bespoke access, security or induction requirements, we set those up at the point of enquiry.",
      },
      {
        q: "Can you take on rolling PPM contracts for Warrington sites?",
        a: "Yes. PPM contracts across mechanical, electrical and controls are typical for Warrington manufacturing and logistics clients; we build the register, agree the schedule and deliver against it.",
      },
      {
        q: "Do you handle reactive breakdown response in Warrington?",
        a: "Yes — reactive callout for line-down electrical, mechanical or controls faults is available. Response performance depends on distance and current availability, and we give an honest ETA on the phone when the callout is logged.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Chester (Cheshire)
  // ---------------------------------------------------------------------------
  chester: {
    slug: "chester",
    name: "Chester",
    region: "cheshire",
    regionName: "Cheshire",
    heroImage: "/gallery/extra-04.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in Chester.",
    subhead:
      "Mechanical, electrical and controls support for Chester and the A55 commercial and industrial corridor — close to our Northwich base.",
    seo: {
      title: "Industrial & Commercial Engineering in Chester",
      description:
        "Mechanical, electrical, controls and compliance engineering for Chester and the A55 industrial and commercial corridor. Close to Lutrom's Northwich base.",
    },
    intro: `Chester's engineering estate is unusual for the region — a smaller manufacturing footprint than nearby Warrington or Runcorn, but a substantial commercial, retail and hospitality estate driven by the tourist economy, and the industrial and logistics belt running west along the A55 towards the Cheshire / Flintshire border. The commercial side operates on retail and hospitality rhythms (trading-hour constraints, out-of-hours attendance for anything disruptive), while the industrial side along the A55 runs on more typical manufacturing patterns. Chester is one of our closer core coverage towns from our Northwich workshop, which usually makes it one of the more responsive coverage areas for both reactive and planned work. Lutrom covers Chester with the full mix — installations, PPM contracts, machine work and compliance testing — and we can help with the multi-site rolling maintenance that most retail estates and hospitality groups need running on a statutory schedule.`,
    typicalWork: [
      "Multi-site EICR / fixed-wire testing across retail and commercial estates",
      "PAT testing for commercial, retail and hospitality sites",
      "Fire-alarm and emergency-lighting servicing to statutory intervals",
      "Industrial electrical installation and rewires",
      "Control-panel work for manufacturing and process sites along the A55",
      "PPM contracts across mechanical, electrical and controls",
    ],
    servicesFocus: [
      "eicr-and-fixed-wire-testing",
      "pat-testing",
      "fire-alarms-and-emergency-lighting",
      "planned-preventive-maintenance",
    ],
    coverageNote:
      "Chester is a straightforward run from our Northwich base via the A556 / A51 or M56. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "Do you cover Chester's retail and hospitality estates?",
        a: "Yes — EICR, PAT, fire-alarm and emergency-lighting testing plus general electrical maintenance is common on our schedule for Chester retail and hospitality sites. Out-of-hours attendance is booked in advance where trading hours make daytime work impractical.",
      },
      {
        q: "Can you cover the A55 industrial corridor?",
        a: "Yes. The industrial and logistics estates along the A55 towards Ellesmere Port and the Flintshire border are inside our normal coverage. We can help with new installations, PPM and reactive support.",
      },
      {
        q: "How responsive can you be for Chester reactive callouts?",
        a: "Chester is one of our closer core coverage towns from our Northwich workshop, which usually makes it one of the more responsive coverage areas. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
      },
      {
        q: "Do you offer multi-site accounts for Chester estates?",
        a: "Yes. Multi-site accounts are consolidated into a single register with per-site schedules and portfolio-level reporting.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Preston (Lancashire)
  // ---------------------------------------------------------------------------
  preston: {
    slug: "preston",
    name: "Preston",
    region: "lancashire",
    regionName: "Lancashire",
    heroImage: "/gallery/extra-06.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in Preston.",
    subhead:
      "Mechanical, electrical and controls support for Preston and the wider Central Lancashire manufacturing and logistics estate.",
    seo: {
      title: "Industrial & Commercial Engineering in Preston",
      description:
        "Mechanical, electrical, controls and compliance engineering for Preston and Central Lancashire manufacturing, logistics and commercial sites.",
    },
    intro: `Preston is the manufacturing and logistics anchor of Central Lancashire — a strong base in food and beverage, aerospace-adjacent manufacturing, tier-two and tier-three automotive supply, logistics along the M6 corridor and a substantial commercial and retail estate through the city. Preston is a longer drive from our Northwich workshop than our closer coverage towns. For sites further from our Northwich base, planned PPM visits can be grouped where this suits the customer and the asset criticality; reactive callouts to Preston are available and typically better suited to critical assets on a PPM contract with pre-agreed priority. Lutrom covers Preston with the full mix of mechanical, electrical and controls engineering — installations, machine work, control-panel builds, PPM contracts and compliance testing.`,
    typicalWork: [
      "PPM contracts across mechanical, electrical and controls",
      "Industrial electrical installation for manufacturing and logistics sites",
      "Machine installation, refurbishment and commissioning",
      "Control-panel design, build and installation",
      "EICR / fixed-wire testing and PAT for commercial estates",
      "Reactive breakdown response for critical assets on PPM contracts",
    ],
    servicesFocus: [
      "planned-preventive-maintenance",
      "machine-installation-and-commissioning",
      "industrial-electrical-installation",
      "eicr-and-fixed-wire-testing",
    ],
    coverageNote:
      "Preston is a longer drive from our Northwich base than our closer coverage towns. For sites further from our Northwich base, planned PPM visits can be grouped where this suits the customer and the asset criticality.",
    faq: [
      {
        q: "How do you cover Preston from Northwich?",
        a: "Preston is a longer drive than our closer coverage towns. For sites further from our Northwich base, planned PPM visits can be grouped where this suits the customer and the asset criticality. Ad-hoc reactive is available but usually better suited to critical assets on a PPM with agreed priority.",
      },
      {
        q: "Can you handle Preston multi-site accounts?",
        a: "Yes. Multi-site accounts (retail estates, hospitality groups, education portfolios) are consolidated into a single register with per-site schedules, and Preston sites slot in with the wider North West coverage.",
      },
      {
        q: "Do you cover the industrial parks around Preston?",
        a: "Yes — the manufacturing and logistics estates around Preston, Leyland and Chorley are inside our Central Lancashire coverage. New installations, PPM and reactive support are all available.",
      },
      {
        q: "What about compliance testing in Preston?",
        a: "EICR, PAT, fire-alarm and emergency-lighting testing are routinely scheduled into Preston client visits. For multi-site portfolios we consolidate testing across all locations into a single cycle with unified reporting.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Salford (Greater Manchester)
  // ---------------------------------------------------------------------------
  salford: {
    slug: "salford",
    name: "Salford",
    region: "greater-manchester",
    regionName: "Greater Manchester",
    heroImage: "/gallery/extra-07.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in Salford.",
    subhead:
      "Mechanical, electrical and controls support for Salford's industrial corridor, commercial estate and media / broadcast cluster.",
    seo: {
      title: "Industrial & Commercial Engineering in Salford",
      description:
        "Mechanical, electrical, controls and compliance engineering for Salford industrial, commercial and media / broadcast sites. Support from Lutrom's Northwich base.",
    },
    intro: `Salford has three distinct engineering territories: the industrial corridor along the Manchester Ship Canal, the commercial and media cluster around Salford Quays and MediaCityUK, and a large logistics and warehousing belt that has grown along the M60 orbital and the neighbouring M62 corridor. Each has a different working pattern — the Ship Canal industrial estate on typical manufacturing hours, the media / broadcast cluster with EMC and signal-integrity considerations, and the logistics belt operating close to 24/7 on peak-season cycles. Lutrom covers Salford and its industrial, commercial and broadcast estate from our Northwich workshop, with the full mix of mechanical, electrical and controls capability under one accountable team. We can help with new installations, control-panel work, PPM contracts, compliance testing, and the reactive callout support that busy logistics operations often need most.`,
    typicalWork: [
      "Industrial electrical installation for logistics, manufacturing and media sites",
      "Control-panel design, build and installation to BS EN 61439",
      "PPM contracts across mechanical, electrical and controls",
      "EICR / fixed-wire testing and PAT for commercial and broadcast estates",
      "Fire-alarm and emergency-lighting servicing to statutory intervals",
      "Reactive breakdown callout for line-down and time-critical faults",
    ],
    servicesFocus: [
      "industrial-electrical-installation",
      "planned-preventive-maintenance",
      "control-panel-design-and-build",
      "eicr-and-fixed-wire-testing",
    ],
    coverageNote:
      "Salford is well-served by the M6/M60/M62 network from our Northwich base. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "Do you cover MediaCityUK and the Salford Quays commercial cluster?",
        a: "Yes — commercial electrical installation, EICR, PAT and reactive support for the Salford Quays and MediaCityUK cluster is inside our normal coverage. Where broadcast or media environments impose EMC or signal-integrity requirements, we scope those into the design and installation.",
      },
      {
        q: "Can you cover the industrial estates along the Ship Canal?",
        a: "Yes. The manufacturing and logistics estates along the Ship Canal industrial corridor are inside our normal coverage, and we can support with new installations, PPM and reactive works.",
      },
      {
        q: "Do you offer 24/7 reactive callout in Salford?",
        a: "Yes — 24/7 reactive callout is available across Greater Manchester. Response performance depends on distance from Northwich and current engineer availability, and we give an honest ETA on the phone when the callout is logged.",
      },
      {
        q: "Can you handle multi-site accounts across Salford and Greater Manchester?",
        a: "Yes. Multi-site accounts across Salford and the wider Greater Manchester footprint are consolidated into a single register with per-site schedules and portfolio-level reporting.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Bolton (Greater Manchester)
  // ---------------------------------------------------------------------------
  bolton: {
    slug: "bolton",
    name: "Bolton",
    region: "greater-manchester",
    regionName: "Greater Manchester",
    heroImage: "/gallery/extra-08.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in Bolton.",
    subhead:
      "Mechanical, electrical and controls support for Bolton's manufacturing and logistics estate and the wider northern Greater Manchester belt.",
    seo: {
      title: "Industrial & Commercial Engineering in Bolton",
      description:
        "Mechanical, electrical, controls and compliance engineering for Bolton manufacturing, logistics and commercial sites. Support from Lutrom's Northwich base.",
    },
    intro: `Bolton has a strong manufacturing and logistics footprint by North West standards — a legacy of light industrial activity that has evolved into a mix of food and beverage production, contract manufacturing, tier-two supply-chain sites, and a large logistics belt built out along the M61 / M60 corridor over the last two decades. The commercial and retail estate through the town and neighbouring Bury adds a compliance-testing rhythm on top of the industrial demand. Bolton is a longer drive from our Northwich workshop than our closer coverage towns. For sites further from our Northwich base, planned PPM visits can be grouped where this suits the customer and the asset criticality, with reactive callouts available for critical assets on PPM. Lutrom covers Bolton with the full mix of mechanical, electrical and controls engineering, plus statutory compliance testing (EICR, PAT, fire-alarm and emergency-lighting).`,
    typicalWork: [
      "PPM contracts across mechanical, electrical and controls",
      "Industrial electrical installation for manufacturing and logistics sites",
      "Machine installation and control-panel work",
      "EICR / fixed-wire testing and PAT for commercial estates",
      "Fire-alarm and emergency-lighting servicing to statutory intervals",
      "Reactive breakdown response for critical assets on PPM contracts",
    ],
    servicesFocus: [
      "planned-preventive-maintenance",
      "industrial-electrical-installation",
      "control-panel-design-and-build",
      "eicr-and-fixed-wire-testing",
    ],
    coverageNote:
      "Bolton is a longer drive from our Northwich base than our closer coverage towns. For sites further from our Northwich base, planned PPM visits can be grouped where this suits the customer and the asset criticality — reactive callouts are available for critical assets on PPM.",
    faq: [
      {
        q: "How do you cover Bolton from Northwich?",
        a: "Bolton is a longer drive than our closer coverage towns. For sites further from our Northwich base, planned PPM visits can be grouped where this suits the customer and the asset criticality. Reactive callouts are available for critical assets on PPM with agreed priority.",
      },
      {
        q: "Do you cover the industrial parks around Bolton?",
        a: "Yes. The manufacturing and logistics estates around Bolton and the M61 corridor are inside our Greater Manchester coverage. New installations, PPM and reactive support are all available.",
      },
      {
        q: "Can you take on multi-site accounts across Bolton?",
        a: "Yes. Multi-site accounts (retail estates, hospitality groups, education portfolios) across Bolton and the wider northern Greater Manchester footprint are consolidated into a single register with unified reporting.",
      },
      {
        q: "What about compliance testing in Bolton?",
        a: "EICR, PAT, fire-alarm and emergency-lighting testing are routinely scheduled into Bolton client visits. For multi-site portfolios we consolidate testing across all locations into a single cycle.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // St Helens (Merseyside)
  // ---------------------------------------------------------------------------
  "st-helens": {
    slug: "st-helens",
    name: "St Helens",
    region: "merseyside",
    regionName: "Merseyside",
    heroImage: "/gallery/db-panel-2.jpg",
    heroPosition: "center 40%",
    h1: "Industrial and commercial engineering in St Helens.",
    subhead:
      "Mechanical, electrical and controls support for St Helens' manufacturing, logistics and commercial estate.",
    seo: {
      title: "Industrial & Commercial Engineering in St Helens",
      description:
        "Mechanical, electrical, controls and compliance engineering for St Helens manufacturing, logistics and commercial sites. Support from Lutrom's Northwich base.",
    },
    intro: `St Helens has a substantial industrial legacy — glass, chemicals, engineering and manufacturing built up around the town over the last century, most of which has evolved into a modern manufacturing and logistics estate. The current footprint mixes food and beverage production, general engineering, chemical process and packaging, plus a strong logistics belt that has grown along the M62 corridor. St Helens sits between our Northwich workshop and Liverpool, which makes it one of our more accessible coverage towns in Merseyside. Lutrom covers St Helens with the full mix of mechanical, electrical and controls engineering — new installations, PPM contracts, machine work and compliance testing — plus the reactive callout support that industrial and logistics operations typically need.`,
    typicalWork: [
      "Industrial electrical installation for manufacturing and logistics sites",
      "Machine installation, refurbishment and commissioning",
      "Control-panel design, build and installation",
      "PPM contracts across mechanical, electrical and controls",
      "EICR / fixed-wire testing and PAT for commercial estates",
      "Reactive breakdown callout for line-down and safety-critical faults",
    ],
    servicesFocus: [
      "industrial-electrical-installation",
      "machine-installation-and-commissioning",
      "planned-preventive-maintenance",
      "24-7-emergency-breakdown-callout",
    ],
    coverageNote:
      "St Helens is a straightforward run from our Northwich base via the M6 or A580. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "How responsive can you be for St Helens reactive callouts?",
        a: "St Helens is one of our more accessible coverage towns in Merseyside, sitting between our Northwich workshop and Liverpool. Response performance depends on time of day and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
      },
      {
        q: "Do you cover the industrial estates around St Helens?",
        a: "Yes. The manufacturing and logistics estates around St Helens and the M62 corridor are inside our normal coverage. New installations, PPM and reactive support are all available.",
      },
      {
        q: "Can you take on PPM contracts for St Helens sites?",
        a: "Yes. PPM contracts across mechanical, electrical and controls are typical for St Helens manufacturing and logistics clients; we build the asset register, agree the schedule and deliver against it.",
      },
      {
        q: "Do you offer multi-site accounts across St Helens and Merseyside?",
        a: "Yes. Multi-site accounts across St Helens and the wider Merseyside footprint are consolidated into a single register with per-site schedules and portfolio-level reporting.",
      },
    ],
  },
};
